import { useState } from "react";
import { BASE_URL } from '../apiConfig';
import { toast } from 'sonner';
import { Eye, Check, Trash2 } from 'lucide-react';
import ConfirmatorRapport from "../cors/ConfirmatorRapport";
import ConfirmatorInvoiceModal from "../cors/ConfirmatorInvoiceModal";
import DeleteModal from '../cors/DeleteModal';
import PaymentMethodModal from "../cors/PaymentMethodModal";
import EditInvoiceModal from "../cors/EditInvoiceModal";
import { useAuth } from "../context/AuthProvider";

export default function ConfirmatorPaymentList({ invoice, role, roles, fetchInvoices }) {
  const { token } = useAuth();
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false); 

  const handleDeleteInvoice = (invoice) => {
    setSelectedInvoice(invoice);
    setIsDeleteModalOpen(true);
  };

  const handleEditInvoice = (invoice) => {
    setSelectedInvoice(invoice);
    setShowEditModal(true);
  };

  const deleteInvoice = async () => {
      if (!selectedInvoice) return;
      
      setIsDeleteModalOpen(false);
      setIsLoading(true);
  
      try {
        const res = await fetch(`${BASE_URL}/api/confirmators-invoices/${selectedInvoice.id}`, {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        });
  
        const data = await res.json();
  
        if (res.ok && data.success) {
          toast.success("Facture supprimée avec succès");
          fetchInvoices(); 
        } else {
          toast.error(data.message || "La suppression a échoué !");
        }
      } catch (error) {
        toast.error("Erreur lors de la suppression de la facture !");
      } finally {
        setIsLoading(false);
      }
    };

  const handleCloseModal = () => {
    setShowDetailsModal(false);
  };
  
  const [editForm, setEditForm] = useState({
    status: invoice.status,
    payment_method: invoice.payment_method || '',
    bank: invoice.bank || '',
    payment_date: invoice.payment_date || ''
  });

  const handleSaveEdit = async (updatedData) => {
      setIsLoading(true);
      
      try {
        const res = await fetch(`${BASE_URL}/api/confirmators-invoices/${selectedInvoice.id}`, {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify(updatedData),
        });
  
        const data = await res.json();
  
        if (res.ok && data.success) {
          toast.success("Facture mise à jour avec succès");
          fetchInvoices();
          setShowEditModal(false);
        } else {
          toast.error(data.message || "La mise à jour a échoué !");
        }
      } catch (error) {
        toast.error("Erreur lors de la mise à jour de la facture !");
      } finally {
        setIsLoading(false);
      }
    };

  const handleMarkAsPaidClick = (invoice) => {
    if (invoice.status === 'paid') {
      toast.info("Cette facture est déjà payée");
      return;
    }
    setSelectedInvoice(invoice);
    setShowPaymentModal(true);
  };

  const handleConfirmPayment = async (paymentMethod) => {
    if (!selectedInvoice) return;
    
    setShowPaymentModal(false);
    setIsLoading(true);
    
    try {
      const res = await fetch(`${BASE_URL}/api/confirmator-invoices/${selectedInvoice.id}/mark-as-paid`, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          payment_method: paymentMethod
        }),
      });

      const data = await res.json();

      if (res.ok && data.success) {
        toast.success("Facture marquée comme payée avec succès");
        fetchInvoices();
      } else {
        toast.error(data.message || "Le marquage comme payé a échoué !");
      }
    } catch (error) {
      toast.error("Erreur lors du marquage de la facture !");
    } finally {
      setIsLoading(false);
    }
  };

  const formatPaymentMethod = (method) => {
    const methodMap = {
      "cash": "Espèces",
      "cheque": "Chèque",
      "bank_transfer": "Virement bancaire",
      "credit_card": "Carte de crédit"
    };
    return methodMap[method] || method || "Non spécifié";
  };

  const formatStatus = (status) => {
    const statusMap = {
      "paid": "Réglé",
      "pending_payment": "Paiement en attente",
      "cancelled": "Annulé"
    };
    return statusMap[status] || status?.charAt(0).toUpperCase() + status?.slice(1) || "Inconnu";
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'bg-green-600 text-white';
      case 'pending_payment': return 'bg-yellow-100 text-yellow-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  function formatDate(isoString) {
    if (!isoString) return "N/A";
    const date = new Date(isoString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  }

  return (
    <>
      <tr className="hover:bg-gray-50 transition-colors">
        <td className="pl-6 pr-2 py-4 whitespace-nowrap">
          <div className="text-xs font-medium text-gray-900">{invoice.invoice_number}</div>
          <div className="text-[10px] text-gray-500">
            {formatDate(invoice.created_at)}
          </div>
        </td>
        
        <td className="px-2 py-4 text-center">
          <div className="text-xs font-medium text-gray-900">
            {invoice.confirmator?.name || 'N/A'}
          </div>
          <div className="text-xs text-gray-500">
            {invoice.confirmator?.email || ''}
          </div>
        </td>
        
        
        <td className="px-2 py-4 text-center">
          <button 
            onClick={() => setShowDetailsModal(true)}
            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-medium bg-blue-100 text-blue-800 hover:bg-blue-200"
          >
            {invoice.order_confirmators_count || 0} orders
          </button>
        </td>
        
        <td className="px-2 py-4 text-center text-sm font-bold text-green-600">
          {parseFloat(invoice.total).toFixed(2)} MAD
        </td>
        
        <td className=" px-2 py-4 text-center text-[11px] text-gray-900">
          {invoice.payment_date ? formatDate(invoice.payment_date) : '-'}
          <div className="text-[10px] text-gray-500">{invoice.payment_method ? "("+formatPaymentMethod(invoice.payment_method)+")" : ''}</div>
        </td>
        
        <td className="px-2 py-4 text-center">
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-medium ${getStatusColor(invoice.status)}`}>
            {formatStatus(invoice.status)}
          </span>
        </td>
        
        <td className="px-1 py-4">
          <div className="flex justify-center gap-1">
            {
              role === 1 || (role === 2 && roles.pay_confirmator_invoices === 1) ? (
                <button 
                  onClick={() => handleMarkAsPaidClick(invoice)}
                  disabled={isLoading || invoice.status === 'paid'}
                  className={`p-2 border-2 cursor-pointer rounded-xl transition-all duration-300 ${
                    invoice.status === 'paid' 
                      ? 'border-green-200 text-green-400 cursor-not-allowed' 
                      : 'border-gray-200 hover:border-[var(--color-primary-500)] text-gray-600 hover:text-[var(--color-primary-500)]'
                  }`}
                  title={invoice.status === 'paid' ? "Déjà payée" : "Marquer comme payé"}
                >
                  <Check className='w-3.5 h-3.5' />
                </button>  
              ): ''
            }

            {invoice && <ConfirmatorRapport invoice={invoice} />}
            {
              role === 1 ? (
                <button
                  onClick={() => handleEditInvoice(invoice)}
                  disabled={isLoading}
                  className="p-2 border-2 cursor-pointer border-gray-200 hover:border-[var(--color-primary-500)] text-gray-600 hover:text-[var(--color-primary-500)] rounded-xl transition-all duration-300 disabled:opacity-50"
                  title="Modifier la facture"
                >
                  <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                    />
                  </svg>
                </button>
              ): ''
            }
            <button
              onClick={() => setShowDetailsModal(true)}
              className="p-2 border-2 cursor-pointer border-gray-200 hover:border-[var(--color-primary-500)] text-gray-600 hover:text-[var(--color-primary-500)] rounded-xl transition-all duration-300"
              title="Voir les détails"
            >
              <Eye className="h-3.5 w-3.5" />
            </button>
            {
              (role === 1 && invoice.status !== "paid") || (role === 2 && roles.delete_confirmator_invoices === 1 && invoice.status !== "paid") ? (
                <button 
                  onClick={() => handleDeleteInvoice(invoice)}
                  disabled={isLoading}
                  className="p-2 border-2 cursor-pointer border-gray-200 hover:border-red-400 text-gray-600 hover:text-red-500 rounded-xl transition-all duration-300 disabled:opacity-50"
                  title="Supprimer la facture"
                  >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              ): ''
            }
          </div>
          {showDetailsModal && (
            <ConfirmatorInvoiceModal 
              isOpen={showDetailsModal}
              onClose={handleCloseModal}
              confirmatorInvoice={invoice} />
          )}
          {showPaymentModal && (
              <PaymentMethodModal
                isOpen={showPaymentModal}
                onClose={() => setShowPaymentModal(false)}
                onConfirm={handleConfirmPayment}
                isLoading={isLoading}
              />
          )}
          {showEditModal && (
            <EditInvoiceModal
              isOpen={showEditModal}
              onClose={() => setShowEditModal(false)}
              invoice={selectedInvoice}
              onSave={handleSaveEdit}
              isLoading={isLoading}
            />
          
          )}
          </td>
          {isDeleteModalOpen && (
            <DeleteModal
              isOpen={isDeleteModalOpen}
              onClose={() => setIsDeleteModalOpen(false)}
              onConfirm={deleteInvoice}
              title="Supprimer la facture"
              message="Êtes-vous sûr de vouloir supprimer cette facture ?"
              itemName={selectedInvoice?.invoice_number}
              isLoading={isLoading}
            />
          )}

      </tr>

    </>
  );
}